import React, { useState } from 'react';
import { ArrowLeft, Download, Calendar, Brain, BookOpen, TrendingUp, TrendingDown, Target, Award, AlertCircle, BarChart3 } from 'lucide-react';
import { StarField } from '../components/Background/StarField';
export function KnowledgePage() {
  const [timeRange, setTimeRange] = useState('week');
  const [selectedSubject, setSelectedSubject] = useState<'all' | 'math' | 'chinese' | 'english'>('all');
  // 模拟数据 - 基于原型图
  const data = {
    // 顶部统计卡片
    overview: {
      totalAssignments: 2847,
      totalStudents: 3086,
      avgCorrectRate: 96,
      avgExcellentRate: 91,
      avgGoodRate: 95,
      aiGradingCount: 15234
    },
    // 学科健康度指数
    subjectHealthIndex: [{
      subject: '语文',
      score: 85,
      trend: 2.3
    }, {
      subject: '数学',
      score: 78,
      trend: -1.5
    }, {
      subject: '英语',
      score: 82,
      trend: 3.1
    }, {
      subject: '物理',
      score: 75,
      trend: 0
    }, {
      subject: '化学',
      score: 88,
      trend: 4.2
    }, {
      subject: '生物',
      score: 80,
      trend: 1.8
    }],
    // 作业质量分析
    assignmentQuality: {
      correctRate: 96,
      excellentRate: 91,
      goodRate: 95,
      passRate: 98
    },
    // 知识点掌握情况
    knowledgePointMastery: [{
      point: '函数与方程',
      masteryRate: 85,
      difficulty: 'medium',
      errorCount: 45
    }, {
      point: '立体几何',
      masteryRate: 72,
      difficulty: 'hard',
      errorCount: 89
    }, {
      point: '概率统计',
      masteryRate: 78,
      difficulty: 'medium',
      errorCount: 67
    }, {
      point: '三角函数',
      masteryRate: 88,
      difficulty: 'easy',
      errorCount: 32
    }, {
      point: '数列',
      masteryRate: 81,
      difficulty: 'medium',
      errorCount: 54
    }, {
      point: '解析几何',
      masteryRate: 76,
      difficulty: 'hard',
      errorCount: 72
    }],
    // 错题共性分析
    commonMistakes: [{
      topic: '二次函数应用',
      errorRate: 28,
      count: 156,
      subject: '数学'
    }, {
      topic: '立体几何证明',
      errorRate: 35,
      count: 198,
      subject: '数学'
    }, {
      topic: '概率计算',
      errorRate: 22,
      count: 124,
      subject: '数学'
    }, {
      topic: '文言文翻译',
      errorRate: 31,
      count: 175,
      subject: '语文'
    }, {
      topic: '完形填空',
      errorRate: 26,
      count: 147,
      subject: '英语'
    }],
    // 学生成绩分布
    scoreDistribution: [{
      range: '90-100分',
      count: 856,
      percentage: 27.7
    }, {
      range: '80-89分',
      count: 1234,
      percentage: 40.0
    }, {
      range: '70-79分',
      count: 678,
      percentage: 22.0
    }, {
      range: '60-69分',
      count: 234,
      percentage: 7.6
    }, {
      range: '60分以下',
      count: 84,
      percentage: 2.7
    }],
    // 教师批改效率
    teacherEfficiency: {
      avgGradingTime: 3.5,
      aiAssistedRate: 85,
      manualReviewRate: 15,
      feedbackQuality: 92
    },
    // 学习进度追踪
    learningProgress: [{
      date: '07-25',
      avgScore: 82
    }, {
      date: '07-26',
      avgScore: 84
    }, {
      date: '07-27',
      avgScore: 83
    }, {
      date: '07-28',
      avgScore: 85
    }, {
      date: '07-29',
      avgScore: 87
    }, {
      date: '07-30',
      avgScore: 86
    }, {
      date: '07-31',
      avgScore: 88
    }],
    // 薄弱知识点排名
    weakKnowledgePoints: [{
      rank: 1,
      point: '立体几何',
      errorRate: 35,
      studentCount: 198
    }, {
      rank: 2,
      point: '文言文翻译',
      errorRate: 31,
      studentCount: 175
    }, {
      rank: 3,
      point: '二次函数应用',
      errorRate: 28,
      studentCount: 156
    }, {
      rank: 4,
      point: '完形填空',
      errorRate: 26,
      studentCount: 147
    }, {
      rank: 5,
      point: '概率计算',
      errorRate: 22,
      studentCount: 124
    }],
    // 各年级作业完成情况
    gradeCompletion: [{
      grade: '七年级',
      completed: 95,
      excellent: 88,
      good: 92
    }, {
      grade: '八年级',
      completed: 93,
      excellent: 85,
      good: 90
    }, {
      grade: '九年级',
      completed: 97,
      excellent: 92,
      good: 95
    }, {
      grade: '高一',
      completed: 94,
      excellent: 87,
      good: 91
    }, {
      grade: '高二',
      completed: 96,
      excellent: 90,
      good: 94
    }, {
      grade: '高三',
      completed: 98,
      excellent: 93,
      good: 96
    }]
  };
  return <div className="relative w-full min-h-screen bg-[#0B1120] overflow-auto" data-id="element-1042">
      {/* Background */}
      <StarField data-id="element-1043" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-[#0B1120] to-[#0B1120]/95 backdrop-blur-sm border-b border-[#38BDF8]/20" data-id="element-1044">
        <div className="px-8 py-4" data-id="element-1045">
          <div className="flex items-center justify-between" data-id="element-1046">
            {/* Left: Back & Title */}
            <div className="flex items-center gap-6" data-id="element-1047">
              <button onClick={() => window.history.back()} className="p-2 glass-panel rounded-lg hover:bg-white/10 transition-colors" data-id="element-1048">
                <ArrowLeft className="w-5 h-5 text-gray-400" data-id="element-1049" />
              </button>
              <div data-id="element-1050">
                <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-1051">
                  <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/20 flex items-center justify-center border border-[#38BDF8]/30" data-id="element-1052">
                    <Brain className="w-6 h-6 text-[#38BDF8]" data-id="element-1053" />
                  </div>
                  易教·助教 智能批改
                </h1>
                <p className="text-sm text-gray-400 mt-1" data-id="element-1054">
                  智能诊断 · 精准施策 · 效果追踪
                </p>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-4" data-id="element-1055">
              <div className="flex items-center gap-2 glass-panel px-3 py-2 rounded-lg border border-white/10" data-id="element-1056">
                <Calendar className="w-4 h-4 text-gray-400" data-id="element-1057" />
                <select value={timeRange} onChange={e => setTimeRange(e.target.value)} className="bg-transparent text-white text-sm focus:outline-none" data-id="element-1058">
                  <option value="week" data-id="element-1059">本周</option>
                  <option value="month" data-id="element-1060">本月</option>
                  <option value="semester" data-id="element-1061">本学期</option>
                </select>
              </div>
              <button className="flex items-center gap-2 glass-panel px-4 py-2 rounded-lg hover:bg-white/10 transition-colors border border-white/10" data-id="element-1062">
                <Download className="w-4 h-4 text-gray-400" data-id="element-1063" />
                <span className="text-white text-sm" data-id="element-1064">导出报告</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-8 py-6" data-id="element-1065">
        {/* Top Stats Bar */}
        <div className="grid grid-cols-6 gap-4 mb-6" data-id="element-1066">
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1067">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1068">作业总数</div>
            <div className="text-3xl font-bold text-[#38BDF8]" data-id="element-1069">
              {data.overview.totalAssignments}
            </div>
          </div>
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1070">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1071">学生总数</div>
            <div className="text-3xl font-bold text-green-400" data-id="element-1072">
              {data.overview.totalStudents}
            </div>
          </div>
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1073">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1074">平均正确率</div>
            <div className="text-3xl font-bold text-purple-400" data-id="element-1075">
              {data.overview.avgCorrectRate}%
            </div>
          </div>
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1076">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1077">优秀率</div>
            <div className="text-3xl font-bold text-orange-400" data-id="element-1078">
              {data.overview.avgExcellentRate}%
            </div>
          </div>
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1079">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1080">良好率</div>
            <div className="text-3xl font-bold text-blue-400" data-id="element-1081">
              {data.overview.avgGoodRate}%
            </div>
          </div>
          <div className="glass-panel p-4 rounded-xl border border-white/10 text-center" data-id="element-1082">
            <div className="text-xs text-gray-400 mb-1" data-id="element-1083">AI批改次数</div>
            <div className="text-3xl font-bold text-pink-400" data-id="element-1084">
              {data.overview.aiGradingCount}
            </div>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-12 gap-6" data-id="element-1085">
          {/* Left Column - Subject Health & Quality */}
          <div className="col-span-3 space-y-6" data-id="element-1086">
            {/* Subject Health Index */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1087">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1088">
                <BookOpen className="w-5 h-5 text-[#38BDF8]" data-id="element-1089" />
                学科健康度指数
              </h3>
              <div className="space-y-4" data-id="element-1090">
                {data.subjectHealthIndex.map((subject, index) => <div key={index} data-id="element-1091">
                    <div className="flex items-center justify-between mb-2" data-id="element-1092">
                      <span className="text-white text-sm" data-id="element-1093">
                        {subject.subject}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-1094">
                        <span className="text-[#38BDF8] font-bold" data-id="element-1095">
                          {subject.score}
                        </span>
                        {subject.trend > 0 && <div className="flex items-center gap-1 text-green-400 text-xs" data-id="element-1096">
                            <TrendingUp className="w-3 h-3" data-id="element-1097" />
                            <span data-id="element-1098">+{subject.trend}</span>
                          </div>}
                        {subject.trend < 0 && <div className="flex items-center gap-1 text-red-400 text-xs" data-id="element-1099">
                            <TrendingDown className="w-3 h-3" data-id="element-1100" />
                            <span data-id="element-1101">{subject.trend}</span>
                          </div>}
                      </div>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden" data-id="element-1102">
                      <div className="h-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] rounded-full" style={{
                    width: `${subject.score}%`
                  }} data-id="element-1103"></div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Assignment Quality */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1104">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1105">
                <Target className="w-5 h-5 text-[#38BDF8]" data-id="element-1106" />
                作业质量分析
              </h3>
              <div className="grid grid-cols-2 gap-3" data-id="element-1107">
                <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-1108">
                  <div className="text-2xl font-bold text-[#38BDF8] mb-1" data-id="element-1109">
                    {data.assignmentQuality.correctRate}%
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-1110">正确率</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-1111">
                  <div className="text-2xl font-bold text-green-400 mb-1" data-id="element-1112">
                    {data.assignmentQuality.excellentRate}%
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-1113">优秀率</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-1114">
                  <div className="text-2xl font-bold text-purple-400 mb-1" data-id="element-1115">
                    {data.assignmentQuality.goodRate}%
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-1116">良好率</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-1117">
                  <div className="text-2xl font-bold text-orange-400 mb-1" data-id="element-1118">
                    {data.assignmentQuality.passRate}%
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-1119">及格率</div>
                </div>
              </div>
            </div>

            {/* Teacher Efficiency */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1120">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1121">
                <Award className="w-5 h-5 text-[#38BDF8]" data-id="element-1122" />
                教师批改效率
              </h3>
              <div className="space-y-4" data-id="element-1123">
                <div className="flex items-center justify-between" data-id="element-1124">
                  <span className="text-gray-400 text-sm" data-id="element-1125">平均批改时间</span>
                  <span className="text-white font-bold" data-id="element-1126">
                    {data.teacherEfficiency.avgGradingTime}min
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1127">
                  <span className="text-gray-400 text-sm" data-id="element-1128">AI辅助率</span>
                  <span className="text-[#38BDF8] font-bold" data-id="element-1129">
                    {data.teacherEfficiency.aiAssistedRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1130">
                  <span className="text-gray-400 text-sm" data-id="element-1131">人工复核率</span>
                  <span className="text-green-400 font-bold" data-id="element-1132">
                    {data.teacherEfficiency.manualReviewRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1133">
                  <span className="text-gray-400 text-sm" data-id="element-1134">反馈质量</span>
                  <span className="text-purple-400 font-bold" data-id="element-1135">
                    {data.teacherEfficiency.feedbackQuality}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Center Column - Knowledge Points & Progress */}
          <div className="col-span-6 space-y-6" data-id="element-1136">
            {/* Knowledge Point Mastery */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1137">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1138">
                <Target className="w-5 h-5 text-[#38BDF8]" data-id="element-1139" />
                知识点掌握情况
              </h3>
              <div className="space-y-3" data-id="element-1140">
                {data.knowledgePointMastery.map((point, index) => <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-id="element-1141">
                    <div className="flex items-center gap-4 flex-1" data-id="element-1142">
                      <span className="text-white font-medium w-32" data-id="element-1143">
                        {point.point}
                      </span>
                      <div className="flex-1" data-id="element-1144">
                        <div className="flex items-center justify-between mb-1" data-id="element-1145">
                          <span className="text-xs text-gray-400" data-id="element-1146">掌握率</span>
                          <span className={`text-sm font-bold ${point.masteryRate >= 80 ? 'text-green-400' : point.masteryRate >= 70 ? 'text-yellow-400' : 'text-red-400'}`} data-id="element-1147">
                            {point.masteryRate}%
                          </span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-1148">
                          <div className={`h-full rounded-full ${point.masteryRate >= 80 ? 'bg-green-400' : point.masteryRate >= 70 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{
                        width: `${point.masteryRate}%`
                      }} data-id="element-1149"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-4" data-id="element-1150">
                      <div className="text-center" data-id="element-1151">
                        <div className="text-xs text-gray-400" data-id="element-1152">错误数</div>
                        <div className="text-sm font-bold text-red-400" data-id="element-1153">
                          {point.errorCount}
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${point.difficulty === 'hard' ? 'bg-red-500/20 text-red-400' : point.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`} data-id="element-1154">
                        {point.difficulty === 'hard' ? '难' : point.difficulty === 'medium' ? '中' : '易'}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Common Mistakes Analysis */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1155">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1156">
                <AlertCircle className="w-5 h-5 text-orange-400" data-id="element-1157" />
                错题共性分析
              </h3>
              <div className="space-y-3" data-id="element-1158">
                {data.commonMistakes.map((mistake, index) => <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-id="element-1159">
                    <div className="flex items-center gap-4 flex-1" data-id="element-1160">
                      <div className="w-8 h-8 rounded-lg bg-orange-400/20 flex items-center justify-center" data-id="element-1161">
                        <span className="text-orange-400 font-bold text-sm" data-id="element-1162">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1" data-id="element-1163">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1164">
                          <span className="text-white font-medium" data-id="element-1165">
                            {mistake.topic}
                          </span>
                          <span className="text-xs text-gray-400 px-2 py-0.5 bg-white/5 rounded" data-id="element-1166">
                            {mistake.subject}
                          </span>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-1167">
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-1168">
                            <div className="h-full bg-gradient-to-r from-red-400 to-orange-400 rounded-full" style={{
                          width: `${mistake.errorRate}%`
                        }} data-id="element-1169"></div>
                          </div>
                          <span className="text-red-400 font-bold text-sm w-12 text-right" data-id="element-1170">
                            {mistake.errorRate}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4" data-id="element-1171">
                      <div className="text-xs text-gray-400" data-id="element-1172">错误人数</div>
                      <div className="text-lg font-bold text-white" data-id="element-1173">
                        {mistake.count}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Learning Progress Tracking */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1174">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1175">
                <TrendingUp className="w-5 h-5 text-[#38BDF8]" data-id="element-1176" />
                学习进度追踪
              </h3>
              <div className="h-48 relative" data-id="element-1177">
                <div className="absolute inset-0 flex items-end justify-between gap-2" data-id="element-1178">
                  {data.learningProgress.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center" data-id="element-1179">
                      <div className="w-full bg-gradient-to-t from-[#38BDF8] to-[#0EA5E9] rounded-t flex items-start justify-center pt-2" style={{
                    height: `${item.avgScore / 100 * 100}%`
                  }} data-id="element-1180">
                        <span className="text-white text-xs font-bold" data-id="element-1181">
                          {item.avgScore}
                        </span>
                      </div>
                      <span className="text-gray-400 text-xs mt-2" data-id="element-1182">
                        {item.date}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Rankings & Distribution */}
          <div className="col-span-3 space-y-6" data-id="element-1183">
            {/* Weak Knowledge Points Ranking */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1184">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1185">
                <AlertCircle className="w-5 h-5 text-[#38BDF8]" data-id="element-1186" />
                薄弱知识点排名
              </h3>
              <div className="space-y-2" data-id="element-1187">
                {data.weakKnowledgePoints.map(point => <div key={point.rank} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" data-id="element-1188">
                    <div className="flex items-center gap-3 flex-1" data-id="element-1189">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${point.rank === 1 ? 'bg-red-400/20 text-red-400' : point.rank === 2 ? 'bg-orange-400/20 text-orange-400' : point.rank === 3 ? 'bg-yellow-400/20 text-yellow-400' : 'bg-[#38BDF8]/20 text-[#38BDF8]'}`} data-id="element-1190">
                        {point.rank}
                      </div>
                      <span className="text-white text-sm" data-id="element-1191">{point.point}</span>
                    </div>
                    <div className="text-right" data-id="element-1192">
                      <div className="text-red-400 font-bold text-sm" data-id="element-1193">
                        {point.errorRate}%
                      </div>
                      <div className="text-gray-400 text-xs" data-id="element-1194">
                        {point.studentCount}人
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Score Distribution */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1195">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1196">
                <BarChart3 className="w-5 h-5 text-[#38BDF8]" data-id="element-1197" />
                学生成绩分布
              </h3>
              <div className="space-y-3" data-id="element-1198">
                {data.scoreDistribution.map((item, index) => <div key={index} data-id="element-1199">
                    <div className="flex items-center justify-between mb-2" data-id="element-1200">
                      <span className="text-gray-400 text-sm" data-id="element-1201">
                        {item.range}
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-1202">
                        {item.percentage}%
                      </span>
                    </div>
                    <div className="h-6 bg-white/5 rounded-full overflow-hidden" data-id="element-1203">
                      <div className="h-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] rounded-full flex items-center justify-end pr-2" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-1204">
                        <span className="text-white text-xs font-bold" data-id="element-1205">
                          {item.count}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Grade Completion */}
            <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-1206">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-1207">
                <Award className="w-5 h-5 text-[#38BDF8]" data-id="element-1208" />
                各年级完成情况
              </h3>
              <div className="space-y-3" data-id="element-1209">
                {data.gradeCompletion.map((grade, index) => <div key={index} className="p-3 bg-white/5 rounded-lg" data-id="element-1210">
                    <div className="text-white font-medium mb-2" data-id="element-1211">
                      {grade.grade}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-1212">
                      <div className="text-center" data-id="element-1213">
                        <div className="text-[#38BDF8] font-bold" data-id="element-1214">
                          {grade.completed}%
                        </div>
                        <div className="text-gray-400" data-id="element-1215">完成率</div>
                      </div>
                      <div className="text-center" data-id="element-1216">
                        <div className="text-green-400 font-bold" data-id="element-1217">
                          {grade.excellent}%
                        </div>
                        <div className="text-gray-400" data-id="element-1218">优秀率</div>
                      </div>
                      <div className="text-center" data-id="element-1219">
                        <div className="text-purple-400 font-bold" data-id="element-1220">
                          {grade.good}%
                        </div>
                        <div className="text-gray-400" data-id="element-1221">良好率</div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>;
}